import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { PortalType } from '../../../config/enum';
import { usePortal } from '../../../contexts/PortalContext/PortalContext';
import { MaintenanceDetails } from '../../../interface/partners-maintenance-scheduling.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { fetchAllMaintenanceSchedules } from '../../../redux/slices/MaintenanceScheduleManagementSlice';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import { fetchAllStationsList } from '../../../redux/slices/Partners/ApprovedLocationManagementSlice';
import { fetchAllDevicesList } from '../../../redux/slices/Partners/DeviceManagementSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomDatePicker from '../../../shared-components/custom-date-range/CustomDatePicker';
import CustomTimeInput from '../../../shared-components/custom-time-input/CustomTimePicker';
import FormCancelWarning from '../../../shared-components/form-cancel-warning/FormCancelWarning';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import MultiSelectDropdown from '../../../shared-components/multi-select-field/MultiSelectField';
import SelectFiled from '../../../shared-components/select-field/SelectField';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';
import FacilityDetailsSidePanel from '../../../shared-components/table-side-panel/FacilityDetailsSidePanel';
import deviceApi from '../../../utils/api/partner/device-partner';
import AddFacilityAccount from '../../account-management/components/add-facility-account';

import MaintenanceContent from './MaintenanceContent';

interface ModalBoxDetail {
  title: string | null;
  content: JSX.Element | null;
  notificationModal: boolean;
}

interface InputInterface {
  label: string;
  value: string;
  description: string;
}

type Option = {
  value: string | number;
  label: string;
};
interface MaintenanceSchedulingProperties {
  onClose?: () => void;
  onSuccess?: () => void;
}
interface AddDeviceProperties {
  _id: string;
  accountCustomId: string;
  accountName: string;
}
interface MaintenanceSchedulingDetails extends MaintenanceDetails {
  accountName?: string;
  accountCustomId?: string;
}

/**
 * Add new maintenance
 * @param {any} param0 - The param0
 * @param {any} param0.onClose - The onClose prop
 * @param {any} param0.onSuccess - The onSuccess prop
 */
function MaintenanceScheduling({ onClose, onSuccess }: MaintenanceSchedulingProperties) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation(['maintenanceSchedule', 'station']);
  const { partnerStations, loading } = useAppSelector((state) => state.partnersApprovedLocation);
  const { allDevices } = useAppSelector((state) => state.partnersDevice);
  const { page, limit } = useAppSelector((state) => state.maintenanceSchedule);
  const { portalType } = usePortal();
  const [allStationsList, setAllStationsList] = useState<InputInterface[]>([]);
  const [accountNameList, setAccountNameList] = useState<InputInterface[]>([]);

  const navigate = useNavigate();
  const [searchParameters, setSearchParameters] = useSearchParams();
  const showFacilityPanel = Boolean(searchParameters.get('accountCustomId'));

  const [maintenanceDetails, setMaintenanceDetails] = useState<MaintenanceSchedulingDetails>({
    accountName: '',
    accountCustomId: '',
    station: [],
    device: [],
    fromDate: '',
    time: '',
    endDate: '',
    endTime: '',
    maintenance: '',
    priority: '',
  });

  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    content: null,
    notificationModal: false,
  });
  const [showFormErrors, setShowFormErrors] = useState(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);

  const typeOfMaintenanceOptions = [
    {
      label: t('inspection'),
      value: 'inspection',
    },
    {
      label: t('repair'),
      value: 'repair',
    },
    {
      label: t('softwareUpdate'),
      value: 'softwareupdate',
    },
  ];
  const priorityOptions = [
    {
      label: t('high'),
      value: 'high',
    },
    {
      label: t('low'),
      value: 'low',
    },
  ];

  /**
   * Handling information icon click
   * @param {any} option - receiving options as a generic
   * @returns {any} - returning options values
   */
  const handleOpenFacilityInformation = (option: Option) => {
    const newParameters = new URLSearchParams(searchParameters);
    newParameters.set('accountCustomId', String(option.value));
    setSearchParameters(newParameters);
  };

  /**
   * Fetch account name list API and set the account name list state with the response data.
   */
  const handleAccountNameList = async () => {
    try {
      const response = await deviceApi.fetchAccountNameList();
      setAccountNameList(
        response.data.map((account: AddDeviceProperties) => ({
          label: account?.accountName,
          value: account?.accountCustomId,
        })),
      );
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message || t('somethingWentWrong'),
          status: true,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    }
  };
  /**
   * Open Add Partner
   */
  const handleOpenAddPartner = () => {
    setModalBoxDetail({
      title: '',
      content: (
        <AddFacilityAccount
          closeModal={(message) => {
            setModalBoxStatus('close');
            if (message) {
              handleAccountNameList();
              dispatch(
                setNotificationMessage({
                  message,
                  status: true,
                  type: 'success',
                }),
              );
            }
          }}
          sidebarClosed={() => setModalBoxStatus('close')}
        />
      ),
      notificationModal: false,
    });
    setModalBoxStatus('open');
  };

  /**
   * Fetch station list based on account ID.
   */
  const handleStationNameList = async () => {
    if (!maintenanceDetails.accountCustomId) return;
    try {
      const response = await deviceApi.fetchChargingStationList(maintenanceDetails.accountCustomId);
      const mappedStations = (response.data || []).map((station: any) => ({
        label: station.name || '',
        value: station._id || station.id || '',
        description: station?.address,
      }));
      setAllStationsList(mappedStations);
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message || t('somethingWentWrong'),
          status: true,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    }
  };

  /**
   * Fetch the all station function
   */
  const fetchStation = () => {
    if (!loading) {
      dispatch(fetchAllStationsList());
    }
  };

  /**
   * Handle input value change
   * @param {string} name - Name of the input
   * @param {any} changeValue - Value of the input
   */
  const handleMaintenanceDetailChange = (name: string, changeValue: string | number | string[]) => {
    setMaintenanceDetails({
      ...maintenanceDetails,
      [name]: changeValue,
    });
    if (name === 'station') {
      if (Array.isArray(changeValue)) {
        dispatch(fetchAllDevicesList({ ids: changeValue }));
      } else if (changeValue) {
        dispatch(fetchAllDevicesList({ ids: [changeValue] }));
      } else {
        dispatch(fetchAllDevicesList({ ids: [] }));
      }
    }
  };

  /**
   * Navigate back
   */
  const handleNavigateBack = () => {
    setShowFormErrors(false);
    setMaintenanceDetails({
      accountName: '',
      accountCustomId: '',
      station: [],
      device: [],
      fromDate: '',
      time: '',
      endDate: '',
      endTime: '',
      maintenance: '',
      priority: '',
    });
    setModalBoxStatus('close');

    if (onClose) {
      onClose();
    } else {
      navigate('../schedule');
    }
  };

  /**
   * Show warning when navigate back
   */
  const handleClickCancel = () => {
    setShowFormErrors(false);
    setModalBoxDetail({
      title: '',
      content: (
        <FormCancelWarning
          title={t('formCancelWarningTitle')}
          description={t('formCancelWarningDescription')}
          cancelButtonLabel={t('cancel')}
          cancelButtonOnClick={() => setModalBoxStatus('close')}
          submitButtonLabel={t('oK')}
          submitButtonOnClick={handleNavigateBack}
        />
      ),
      notificationModal: true,
    });
    setModalBoxStatus('open');
  };

  /**
   * Checks if all fields in the maintenance details are filled
   */
  const isFormComplete = () => {
    if (PortalType.PARTNER !== portalType) {
      if (!maintenanceDetails.accountName || !maintenanceDetails.accountCustomId) {
        return false;
      }
    }
    return (
      maintenanceDetails.station.length > 0 &&
      maintenanceDetails.device.length > 0 &&
      maintenanceDetails.fromDate !== '' &&
      maintenanceDetails.time !== '' &&
      maintenanceDetails.endDate !== '' &&
      maintenanceDetails.endTime !== '' &&
      maintenanceDetails.maintenance !== '' &&
      maintenanceDetails.priority !== ''
    );
  };

  /**
   * Function to show success message
   */
  const openSuccessModal = () => {
    setModalBoxStatus('open');
    setModalBoxDetail({
      title: '',
      content: (
        <SuccessNotification
          title={t('successNotificationTitle')}
          description=""
          notificationCloseTimer={3000}
          onNotificationClose={() => {
            setModalBoxStatus('close');
            if (onSuccess) {
              onSuccess();
            } else {
              dispatch(
                fetchAllMaintenanceSchedules({
                  page,
                  limit,
                }),
              );
            }
            if (onClose) {
              onClose();
            } else {
              navigate('../maintenance');
            }
          }}
        />
      ),
      notificationModal: true,
    });
  };

  /**
   * Submit to open the modal
   */
  const handleSubmitMaintenanceDetails = () => {
    setShowFormErrors(true);
    if (!isFormComplete()) return;

    setModalBoxDetail({
      title: t('maintenanceScheduled'),
      content: (
        <MaintenanceContent
          maintenanceDetails={maintenanceDetails}
          onChangeValue={handleMaintenanceDetailChange}
          closeModal={() => setModalBoxStatus('close')}
          openSuccessModal={openSuccessModal}
          allStationsList={allStationsList}
        />
      ),
      notificationModal: true,
    });
    setModalBoxStatus('open');
  };

  useEffect(() => {
    if (PortalType.PARTNER === portalType) {
      if (partnerStations.length === 0) {
        fetchStation();
      }
    } else {
      handleAccountNameList();
    }
  }, [portalType]);

  useEffect(() => {
    if (maintenanceDetails?.accountName) {
      setMaintenanceDetails((previousDetails) => ({
        ...previousDetails,
        accountCustomId:
          accountNameList.find((account) => account.value === maintenanceDetails.accountName)
            ?.value || '',
        station: [],
        device: [],
      }));
    }
  }, [maintenanceDetails?.accountName, accountNameList]);

  useEffect(() => {
    if (maintenanceDetails.accountCustomId && maintenanceDetails.accountCustomId.length === 20) {
      handleStationNameList();
    } else {
      setAllStationsList([]);
    }
  }, [maintenanceDetails.accountCustomId]);

  // Cleanup selected devices when the available devices list changes
  useEffect(() => {
    const availableDeviceValues = allDevices.map((device) => device.value.toString());
    const updatedSelectedDevices = maintenanceDetails.device.filter((selectedDevice) =>
      availableDeviceValues.includes(selectedDevice),
    );

    if (updatedSelectedDevices.length !== maintenanceDetails.device.length) {
      setMaintenanceDetails((previousDetails) => ({
        ...previousDetails,
        device: updatedSelectedDevices,
      }));
    }
  }, [allDevices]);

  if (showFacilityPanel) {
    return (
      <FacilityDetailsSidePanel
        actionActive
        isEditMode={isEditMode}
        onEditModeChange={setIsEditMode}
        fetchIdFrom="queryParams"
        onBack={() => {
          const newParameters = new URLSearchParams(searchParameters);
          newParameters.delete('accountCustomId');
          setSearchParameters(newParameters);
        }}
      />
    );
  }

  return (
    <div className="add-maintenance-wrap">
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        notification={modalBoxDetail.notificationModal}
        header={!modalBoxDetail.notificationModal}
      />
      <p className="add-maintenance-title">{t('addSchedule')}</p>
      <div className="add-maintenance-container">
        <div className="add-maintenance-from-wrap">
          <div className="add-maintenance-from">
            {PortalType.PARTNER !== portalType && (
              <SelectFiled
                name="accountName"
                inputLabel={t('accountName')}
                placeholder={t('enterAccountName')}
                options={accountNameList}
                showInformationIcon
                onInformationClick={(options) => handleOpenFacilityInformation(options)}
                description={(option) => option.value}
                selectedValue={maintenanceDetails?.accountName ?? ''}
                onChangeValue={handleMaintenanceDetailChange}
                error={!maintenanceDetails.accountName && showFormErrors}
                errorMessage={t('accountNameRequired')}
                valueRequired
                enableSearch
                emptyActionLabel={t('station:addAccount') || 'Add Partner'}
                onEmptyActionClick={handleOpenAddPartner}
              />
            )}
            <MultiSelectDropdown
              title={t('stationName')}
              name="station"
              placeholder={t('chooseStation')}
              placeholderFilled={t('stations')}
              selectedValues={maintenanceDetails.station}
              onSelectionChange={handleMaintenanceDetailChange}
              options={PortalType.PARTNER !== portalType ? allStationsList : partnerStations}
              error={maintenanceDetails.station.length === 0 && showFormErrors}
              errorMessage={t('stationNameIsRequired')}
              search
            />
            <MultiSelectDropdown
              title={t('device')}
              name="device"
              placeholder={t('chooseDevice')}
              placeholderFilled={t('devices')}
              selectedValues={maintenanceDetails.device}
              onSelectionChange={handleMaintenanceDetailChange}
              options={allDevices}
              error={maintenanceDetails.device.length === 0 && showFormErrors}
              errorMessage={t('deviceIsRequired')}
              search
            />
          </div>
          <div className="add-maintenance-from" style={{ marginTop: '15px' }}>
            <CustomDatePicker
              name="fromDate"
              inputLabel={t('startDate', 'Start Date')}
              value={maintenanceDetails.fromDate}
              onChangeValue={handleMaintenanceDetailChange}
              error={!maintenanceDetails.fromDate && showFormErrors}
              errorMessage={t('invalidDateRange')}
              disablePastDates
            />
            <CustomTimeInput
              name="time"
              inputLabel={t('startTime', 'Start Time')}
              value={maintenanceDetails.time}
              onChangeValue={handleMaintenanceDetailChange}
              errorHandle={!maintenanceDetails.time && showFormErrors}
            />
            <CustomDatePicker
              name="endDate"
              inputLabel={t('endDate', 'End Date')}
              value={maintenanceDetails.endDate}
              onChangeValue={handleMaintenanceDetailChange}
              error={!maintenanceDetails.endDate && showFormErrors}
              errorMessage={t('invalidDateRange')}
              disablePastDates
            />
            <CustomTimeInput
              name="endTime"
              inputLabel={t('endTime', 'End Time')}
              value={maintenanceDetails.endTime}
              onChangeValue={handleMaintenanceDetailChange}
              errorHandle={!maintenanceDetails.endTime && showFormErrors}
            />
            <SelectFiled
              inputLabel={t('typeOfMaintenance')}
              name="maintenance"
              placeholder={t('chooseTypeOfMaintenance')}
              selectedValue={maintenanceDetails.maintenance}
              onChangeValue={handleMaintenanceDetailChange}
              options={typeOfMaintenanceOptions}
              error={!maintenanceDetails.maintenance && showFormErrors}
              errorMessage={t('typeOfMaintenanceIsRequired')}
            />
            <SelectFiled
              inputLabel={t('priority')}
              name="priority"
              placeholder={t('choosePriority')}
              selectedValue={maintenanceDetails.priority}
              onChangeValue={handleMaintenanceDetailChange}
              options={priorityOptions}
              error={!maintenanceDetails.priority && showFormErrors}
              errorMessage={t('priorityIsRequired')}
            />
          </div>
        </div>
        <div className="add-maintenance-button-wrap">
          <div className="form-buttons-wrap">
            <MyButton label={t('cancel')} buttonType="back" onClickFunc={handleClickCancel} />
            <MyButton
              label={t('schedule')}
              buttonType="submit"
              onClickFunc={handleSubmitMaintenanceDetails}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default MaintenanceScheduling;

MaintenanceScheduling.defaultProps = {
  /**
   * Function to handle close
   */
  onClose: () => {},
  /**
   * Function to handle success
   */
  onSuccess: () => {},
};
